/**
 * Frozen monthly snapshots from `property_log`, for the Properties section.
 *
 * One workspace-scoped query (no per-property fan-out) returns every snapshot
 * row; consumers group them by property and feed `buildPropertyMonths` along
 * with the live current-month inputs from `useOwnedProperties`.
 *
 * Past months are READ from here; the current month is computed live; future
 * months are never produced (see `buildPropertyMonths`).
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { PostgrestSingleResponse } from "@supabase/supabase-js";

import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/hooks/use-workspace";
import type { PropertyLogRow } from "@/components/properties/cashflow-metrics";

/** Columns the monthly resolver needs (a subset of property_log). */
const LOG_COLUMNS =
  "pipeline_property_id, year, month, month_name, loan_type, loan_started, " +
  "purchase_price, after_repair_value, monthly_rent, rent_amount, monthly_cash_flow, " +
  "flip_hard_money, flip_holding_cost, brrrr_hard_money, brrrr_long_term, rental_long_term";

export function usePropertyLogs() {
  const { currentWorkspace } = useWorkspace();
  const workspaceId = currentWorkspace?.id;

  const query = useQuery({
    queryKey: ["property_logs", workspaceId] as const,
    enabled: Boolean(workspaceId),
    queryFn: async () => {
      if (!workspaceId) return [] as PropertyLogRow[];
      // `property_log` isn't in the generated Supabase types yet (added by
      // migration 20260616000000). Cast through a minimal structural shape —
      // the same pattern use-owned-properties uses for not-yet-generated tables.
      const client = supabase as unknown as {
        from: (t: string) => {
          select: (s: string) => {
            eq: (
              c: string,
              v: string,
            ) => Promise<PostgrestSingleResponse<PropertyLogRow[]>>;
          };
        };
      };
      const { data, error } = (await client
        .from("property_log")
        .select(LOG_COLUMNS)
        .eq("workspace_id", workspaceId)) as PostgrestSingleResponse<PropertyLogRow[]>;
      if (error) throw error;
      return data ?? [];
    },
  });

  // Group rows by property for O(1) lookup in the page.
  const byProperty = useMemo(() => {
    const map = new Map<string, PropertyLogRow[]>();
    for (const row of query.data ?? []) {
      const list = map.get(row.pipeline_property_id);
      if (list) list.push(row);
      else map.set(row.pipeline_property_id, [row]);
    }
    return map;
  }, [query.data]);

  return {
    logsByProperty: byProperty,
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
}
